/**
 * The URL checks behind `<RichText>`, kept free of React so they can be tested
 * on their own (`tests/rich-text-urls.test.mjs`). See `rich-text.tsx` for why
 * every `href` and `src` is checked rather than trusted.
 */

/** Schemes a link may navigate to. `data:` is deliberately not one of them. */
const LINK_SCHEMES = ["http:", "https:", "mailto:"];

/** Schemes an image may load from, alongside {@link DATA_IMAGE}. */
const IMAGE_SCHEMES = ["http:", "https:"];

/**
 * The embedded-screenshot case, and only that case.
 *
 * `image/svg+xml` is excluded on purpose: SVG is a document format rather than
 * a bitmap, Ivanti's editor never emits one, and leaving it out costs nothing.
 */
const DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp|bmp|avif);base64,[A-Za-z0-9+/=\s]*$/i;

/**
 * A note's inline picture, served by this app (`GET /tickets/{id}/inline-images/{RecId}`).
 *
 * The one relative `src` accepted. The backend writes it in place of the
 * Ivanti tenant's attachment link — which the reader's browser cannot open —
 * after sanitising, so a ticket author cannot produce it: any relative `src`
 * they write is dropped server-side first. The path is fixed and the id is a
 * 32-hex Attachment RecId, and the route itself re-checks that the reader may
 * see the ticket and that a public note on it shows this picture.
 */
const INLINE_IMAGE_PATH = /^\/api\/tickets\/[A-Za-z0-9%._~-]+\/inline-images\/[0-9A-F]{32}$/;

/**
 * Shown where a picture cannot be: never an empty gap where content was
 * (LIVE incident 656, 7 Oct). Matches the backend's `richtext.IMAGE_PLACEHOLDER`.
 */
export const IMAGE_PLACEHOLDER = "[image]";

// ─── Attribute checks ────────────────────────────────────────────────────

/**
 * An absolute URL, or nothing.
 *
 * Deliberately parsed with no base, so a relative URL is rejected rather than
 * resolved against this app. Ivanti's markup only ever carries `data:` images
 * and absolute links, so nothing real is lost — and a relative one would
 * otherwise become a same-origin request this app's own session cookie is sent
 * with, on a URL a ticket author chose.
 */
function absoluteUrl(raw: string | null): URL | null {
  if (raw === null || raw.trim() === "") {
    return null;
  }
  try {
    return new URL(raw.trim());
  } catch {
    return null;
  }
}

export function safeHref(raw: string | null): string | undefined {
  const url = absoluteUrl(raw);
  return url !== null && LINK_SCHEMES.includes(url.protocol) ? url.href : undefined;
}

export function safeImageSrc(raw: string | null): string | undefined {
  if (raw === null) {
    return undefined;
  }
  if (DATA_IMAGE.test(raw.trim()) || INLINE_IMAGE_PATH.test(raw.trim())) {
    return raw.trim();
  }
  const url = absoluteUrl(raw);
  return url !== null && IMAGE_SCHEMES.includes(url.protocol) ? url.href : undefined;
}
